import React from 'react';
import { cn } from '@/utils';

export interface LoadingSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'white' | 'current';
}

const LoadingSpinner = React.forwardRef<HTMLDivElement, LoadingSpinnerProps>(
  ({ className, size = 'md', color = 'primary', ...props }, ref) => {
    return (
      <div
        className={cn(
          'inline-block border-2 border-t-transparent rounded-full animate-spin',
          
          // Sizes
          {
            'w-4 h-4 border-2': size === 'sm',
            'w-6 h-6 border-2': size === 'md',
            'w-8 h-8 border-3': size === 'lg',
          },
          
          // Colors
          {
            'border-primary-500': color === 'primary',
            'border-white': color === 'white',
            'border-current': color === 'current',
          },
          
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

LoadingSpinner.displayName = 'LoadingSpinner';

export interface LoadingScreenProps {
  message?: string;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ message = 'Loading...' }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-tg-text text-center">{message}</p>
    </div>
  );
};

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string | number;
  height?: string | number;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant = 'text', width, height, style, ...props }, ref) => {
    return (
      <div
        className={cn(
          'bg-neutral-800 animate-pulse',
          {
            'rounded h-4': variant === 'text',
            'rounded-lg': variant === 'rectangular',
            'rounded-full': variant === 'circular',
          },
          className
        )}
        style={{
          width: width,
          height: height,
          ...style,
        }}
        ref={ref}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export { LoadingSpinner, LoadingScreen, Skeleton };
